import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SkeletonLoader from '../components/SkeletonLoader';
import { fetchCategories, fetchMovies, fetchMoviesByCategory, fetchVisitCount, incrementVisitCount } from '../api'; // 确保导入 incrementVisitCount

const MovieList = lazy(() => import('../components/MovieList')); // 确保这是经过懒加载优化的版本

// 防抖函数
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

function HomePageWithCategories() {
  const [categories, setCategories] = useState([]);
  const [allMovies, setAllMovies] = useState([]); // 存储所有电影，用于重置和模糊匹配
  const [movies, setMovies] = useState([]); // 当前显示的电影
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // 新增的状态，用于存储搜索查询
  const [isNavVisible, setIsNavVisible] = useState(false); // 控制导航栏的显示状态
  const [visitCount, setVisitCount] = useState(0); // 新增的状态，用于存储访问次数

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const cachedCategories = JSON.parse(localStorage.getItem('categories'));
    const cachedMovies = JSON.parse(localStorage.getItem('allMovies'));
    const categoriesTimestamp = localStorage.getItem('categoriesTimestamp');
    const moviesTimestamp = localStorage.getItem('moviesTimestamp');

    const fetchData = async () => {
      try {
        let categoriesData, moviesData;

        if (cachedCategories && isWithinOneHour(categoriesTimestamp)) {
          setCategories(cachedCategories);
          categoriesData = cachedCategories;
        } else {
          console.log('Fetching categories...');
          categoriesData = await fetchCategories();
          localStorage.setItem('categories', JSON.stringify(categoriesData));
          localStorage.setItem('categoriesTimestamp', Date.now());
          setCategories(categoriesData);
        }

        if (cachedMovies && isWithinOneHour(moviesTimestamp)) {
          setAllMovies(cachedMovies);
          moviesData = cachedMovies;
        } else {
          console.log('Fetching movies...');
          moviesData = await fetchMovies();
          localStorage.setItem('allMovies', JSON.stringify(moviesData));
          localStorage.setItem('moviesTimestamp', Date.now());
          setAllMovies(moviesData);
          setMovies(moviesData); // 初始设置 movies
        }

        if (location.pathname.includes('/category/')) {
          const categoryId = parseInt(location.pathname.split('/').pop(), 10);
          console.log('Selected category id:', categoryId);
          setSelectedCategory(categoryId);
          const categoryMovies = await fetchMoviesByCategory(categoryId);
          setMovies(categoryMovies);
        }

        // 获取访问次数
        //const countData = await fetchVisitCount();
        //setVisitCount(countData);

        // 递增访问次数
        //await incrementVisitCount();
      } catch (err) {
        console.error('Error fetching data:', err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location]);

  const handleSearchChangeDebounced = useCallback(debounce((query) => {
    setSearchQuery(query);
  }, 300), []); // 300ms 延迟

  useEffect(() => {
    const filterMovies = () => {
      if (!searchQuery) {
        // 如果没有搜索查询，则显示所有电影或当前分类下的电影
        if (selectedCategory !== null) {
          fetchMoviesByCategory(selectedCategory).then(setMovies).catch(console.error);
        } else {
          setMovies(allMovies);
        }
      } else {
        // 根据搜索查询过滤电影
        const filteredMovies = allMovies.filter(movie =>
          movie.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          movie.description.toLowerCase().includes(searchQuery.toLowerCase())
          // 可以继续添加其他字段的模糊匹配条件
        );
        setMovies(filteredMovies);
      }
    };

    filterMovies();
  }, [searchQuery, selectedCategory, allMovies]);

  const handleCategorySelect = async (id) => {
    console.log('Handling category select:', id);
    setSelectedCategory(id);
    navigate(`/category/${id}`);
    
    try {
      const categoryMovies = await fetchMoviesByCategory(id);
      setMovies(categoryMovies);
      setSearchQuery(''); // 清空搜索框
    } catch (error) {
      console.error('Failed to fetch movies by category:', error);
    }
    setIsNavVisible(false); // 收起导航栏
  };

  const handleSearchChange = (event) => {
    handleSearchChangeDebounced(event.target.value);
  };

  const handleMouseEnter = () => {
    setIsNavVisible(true);
  };

  const handleMouseLeave = () => {
    setIsNavVisible(false);
  };

  const handleAllVideosClick = () => {
    setSelectedCategory(null);
    setMovies(allMovies); // 重置为所有电影
    setSearchQuery(''); // 清空搜索框
    setIsNavVisible(false); // 收起导航栏
  };

  // 检查时间戳是否在一小时内
  const isWithinOneHour = (timestamp) => {
    if (!timestamp) return false;
    const now = Date.now();
    const diffTime = now - timestamp;
    const diffHours = diffTime / (1000 * 60 * 60);
    return diffHours <= 1;
  };

  if (loading) return <SkeletonLoader />;
  if (error) return <p>Error: {error}</p>;

  return (
    <div>
      {/* 主页标题 */}
      <h6 style={{ position: 'fixed', top: '10px', left: '20px', zIndex: 1001 }}>学英语 longjianwei@gmail.com龙剑伟</h6>
      {/* 导航条 */}
      <div 
        style={{
          position: 'fixed',
          top: '50px', // 调整位置使其位于标题下方
          left: '0',
          width: '100%',
          backgroundColor: '#4CAF50', // 使用主题颜色
          zIndex: 1000, // 确保导航栏在其他内容之上
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px'
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* 所有视频链接 */}
        <Link
          to="/"
          onClick={handleAllVideosClick}
          className={selectedCategory === null ? 'active' : ''}
          style={{ color: '#fff', textDecoration: 'none', fontSize: '18px', padding: '5px' }}
        >
          视频分类
        </Link>

        {/* 类别导航 */}
        <nav 
          style={{
            position: 'absolute',
            top: '40px', // 调整位置使其位于“所有视频”链接下方
            left: 0,
            backgroundColor: '#4CAF50',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '5px',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
            zIndex: 999,
            display: isNavVisible ? 'block' : 'none'
          }}
        >
          <ul style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
            {categories.map((category) => (
              <li key={category.id} style={{ marginBottom: '5px' }}>
                <Link
                  to={`/movies/category/${category.id}`}
                  onClick={() => handleCategorySelect(category.id)}
                  className={selectedCategory === category.id ? 'active' : ''}
                  style={{ color: '#fff', textDecoration: 'none', display: 'block', padding: '5px' }}
                >
                  {category.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* 搜索框 */}
        <div style={{ marginLeft: 'auto' }}>
          <input
            type="text"
            placeholder="查找..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={{ padding: '8px', width: '300px' }}
          />
        </div>

       
      </div>

      {/* 电影列表 */}
      <div style={{ marginTop: '100px', marginLeft: '200px' }}>
        <Suspense fallback={<SkeletonLoader />}>
          <MovieList movies={movies} />
        </Suspense>
      </div>
    </div>
  );
}

export default HomePageWithCategories;